<template>
  <div class="section">
    <b-loading v-if="isloading" :is-full-page="true"/>

    <div v-else class="container">
      <div class="buttons is-right">
        <b-button type="is-primary" :disabled="!hasMvts" @click="exportScanMvts">
          {{ $t('global.export') }}
        </b-button>
      </div>

      <ag-grid-vue
        style="width: 100%; height: 1000px;"
        class="ag-theme-alpine"
        :modules="modules"
        :pagination="true"
        :pagination-auto-page-size="true"
        :group-display-type="groupDisplayType"
        :grid-options="gridOptions"
        :row-data="mvts"
        :column-defs="columnDefs"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import XLSX from 'xlsx'
import ShippingManagement from '@/services/v2/shippingManagement'

export default {
  data () {
    return {
      modules: AllModules,
      groupDisplayType: 'custom',
      isloading: false,
      mvts: null
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      selectedCustomer: state => state.selectedCustomer
    }),
    gridOptions () {
      return {
        autoGroupColumnDef: {
          headerName: this.$t('customer.scan_mvt.grid.date'),
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        getContextMenuItems: this.getContextMenuItems
      }
    },
    columnDefs () {
      return [
        { headerName: this.$t('customer.scan_mvt.grid.date'),
          field: 'scannedDate',
          filter: 'agDateColumnFilter',
          filterParams: {
            comparator: this.compareDateFilter,
            suppressAndOrCondition: true
          },
          rowGroup: true,
          hide: true
        },
        { headerName: this.$t('customer.scan_mvt.grid.barcode'),
          field: 'barcode',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: this.$t('customer.scan_mvt.grid.scanned_at'),
          field: 'scannedAt',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: this.$t('customer.scan_mvt.grid.updated_at'),
          field: 'updatedAt',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        }
      ]
    },
    hasMvts () {
      return !!(this.mvts && this.mvts.length)
    }
  },
  watch: {
    selectedCustomer (newValue, oldValue) {
      this.loadData()
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      if (!this.selectedCustomer) return
      this.isloading = true
      try {
        const mvts = await ShippingManagement.getScanMvts(this.selectedCustomer)
        this.mvts = mvts.map(mvt => ({
          ...mvt,
          rawScannedAt: mvt.scannedAt,
          rawUpdatedAt: mvt.updatedAt,
          scannedDate: this.formatLocalDate(mvt.scannedAt),
          scannedAt: this.formatLocalDateTime(mvt.scannedAt),
          updatedAt: this.formatLocalDateTime(mvt.updatedAt)
        }))
      } catch (error) {
        this.$buefy.toast.open({
          message: this.$t('customer.scan_mvt.errors.load_data'),
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.isloading = false
      }
    },
    formatLocalDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(navigator.language)
    },
    formatLocalDateTime (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString(navigator.language)
    },
    compareDateFilter (filterLocalDateAtMidnight, cellValue) {
      const cellDate = new Date(cellValue)
      if (Number.isNaN(cellDate.getTime())) return -1

      const cellDateAtMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate())
      if (cellDateAtMidnight.getTime() === filterLocalDateAtMidnight.getTime()) return 0
      return cellDateAtMidnight < filterLocalDateAtMidnight ? -1 : 1
    },
    getContextMenuItems (params) {
      const menuItems = (params.defaultItems || []).filter(item => item !== 'export')
      const mvtRow = params.node && params.node.group
        ? this.mvts.find(mvt => this.getMvtGroupDate(mvt) === params.node.key)
        : params.node && params.node.data

      if (!mvtRow) return menuItems

      return [
        ...menuItems,
        'separator',
        {
          name: this.$t('global.export'),
          action: () => this.exportScanMvtGroup(mvtRow)
        }
      ]
    },
    exportScanMvts () {
      if (!this.hasMvts) return

      this.writeScanMvtsExport(this.mvts)
    },
    exportScanMvtGroup (mvtRow) {
      const groupDate = this.getMvtGroupDate(mvtRow)
      if (!groupDate) return
      this.writeScanMvtsExport(this.mvts.filter(mvt => this.getMvtGroupDate(mvt) === groupDate))
    },
    writeScanMvtsExport (mvts) {
      const columns = [
        { header: this.$t('customer.scan_mvt.grid.date'), field: 'scannedDate' },
        { header: this.$t('customer.scan_mvt.grid.barcode'), field: 'barcode' },
        { header: this.$t('customer.scan_mvt.grid.scanned_at'), field: 'scannedAt' },
        { header: this.$t('customer.scan_mvt.grid.updated_at'), field: 'updatedAt' }
      ]
      const data = [
        columns.map(column => column.header),
        ...mvts.map(mvt => columns.map(column => {
          if (column.field === 'scannedDate') return this.formatLocalDate(mvt.rawScannedAt)
          if (column.field === 'scannedAt') return this.formatLocalDateTime(mvt.rawScannedAt)
          if (column.field === 'updatedAt') return this.formatLocalDateTime(mvt.rawUpdatedAt)
          return mvt[column.field] || ''
        }))
      ]
      const worksheet = XLSX.utils.aoa_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      const fileDate = new Date().toISOString().slice(0, 10)

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Scan movements')
      XLSX.writeFile(workbook, `GPS-${this.$t('sidebar.scan_mvt')}-${fileDate}.csv`)
    },
    getMvtGroupDate (mvt) {
      return mvt ? mvt.scannedDate : ''
    }
  }
}
</script>
